import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import StatusChip from '../TemplateEditor/components/StatusChip';
import { OUTPUT_FORMATS } from '../../config/constants';

/**
 * Common TemplateVersionSelect Component
 *
 * A reusable dropdown component for selecting template versions with:
 * - Status chip display
 * - Date information display
 * - Optional tooltip with model configuration and security policies
 * - Support for different version data structures
 *
 * @param {Object} props
 * @param {Array} props.versions - Array of version objects
 * @param {string|number} props.value - Selected version value
 * @param {Function} props.onChange - Handler for version change: (versionValue, versionObject) => void
 * @param {string} props.label - Label for the select (default: "Template Version")
 * @param {boolean} props.showTooltip - Whether to show tooltip with model config (default: false)
 * @param {Array} props.securityPolicies - Security policies for tooltip (optional)
 * @param {string} props.size - Size of the select (default: "small")
 * @param {boolean} props.fullWidth - Whether select should be full width (default: true)
 * @param {boolean} props.displayEmpty - Whether to allow empty selection (default: false)
 * @param {Function} props.getVersionValue - Custom function to extract version value (optional)
 * @param {Function} props.getVersionNumber - Custom function to extract version number (optional)
 * @param {Function} props.getVersionStatus - Custom function to extract version status (optional)
 * @param {Function} props.getVersionDate - Custom function to extract version date (optional)
 */
const TemplateVersionSelect = ({
  versions = [],
  value,
  onChange,
  label = 'Template Version',
  showTooltip = false,
  securityPolicies = [],
  size = 'small',
  fullWidth = true,
  displayEmpty = false,
  getVersionValue,
  getVersionNumber,
  getVersionStatus,
  getVersionDate,
}) => {
  const theme = useTheme();
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Helper functions to extract version properties (with fallbacks for different data structures)
  const getVersionValueDefault = useCallback(
    version => {
      if (getVersionValue) return getVersionValue(version);
      return version.version_number || version.version || version.id || '';
    },
    [getVersionValue]
  );

  const getVersionNumberDefault = useCallback(
    version => {
      if (getVersionNumber) return getVersionNumber(version);
      return version.version_number || version.version || '';
    },
    [getVersionNumber]
  );

  const getVersionStatusDefault = useCallback(
    version => {
      if (getVersionStatus) return getVersionStatus(version);
      return version.status || null;
    },
    [getVersionStatus]
  );

  const getVersionDateDefault = useCallback(
    version => {
      if (getVersionDate) return getVersionDate(version);
      return version.created_at || version.createdAt || null;
    },
    [getVersionDate]
  );

  // Get status badge color
  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'deprecated':
        return 'default';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format model configuration for tooltip
  const formatModelConfig = useCallback(version => {
    const rawConfig = version?.llm_config || version?.model_config;
    if (!rawConfig) {
      return { provider: 'N/A', model: 'N/A' };
    }

    let parsed = rawConfig;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { provider: 'N/A', model: 'N/A' };
      }
    }

    return {
      provider: parsed.provider || 'N/A',
      model: parsed.model_name || 'N/A',
      temperature: parsed.temperature,
      max_tokens: parsed.max_tokens,
      top_p: parsed.top_p,
      frequency_penalty: parsed.frequency_penalty,
      presence_penalty: parsed.presence_penalty,
    };
  }, []);

  // Format security policies for tooltip
  const formatSecurityPolicies = useCallback(() => {
    if (!securityPolicies || securityPolicies.length === 0) {
      return [];
    }

    return securityPolicies
      .filter(a => a.enabled !== false)
      .map(a => ({
        name: a.policy_name || a.name || 'Unnamed Policy',
        action: a.action_on_violation || 'anonymize',
        enabled: a.enabled !== false,
      }));
  }, [securityPolicies]);

  // Create tooltip content for version
  const createVersionTooltip = useCallback(
    version => {
      if (!showTooltip) return null;

      const modelConfig = formatModelConfig(version);
      const policies = formatSecurityPolicies();

      // Apple-style colors from theme
      const isDark = theme.palette.mode === 'dark';
      const bgColor = isDark
        ? theme.palette.grey[900]
        : theme.palette.background.paper;
      const textPrimary = theme.palette.text.primary;
      const textSecondary = theme.palette.text.secondary;
      const primaryColor = theme.palette.primary.main;
      const secondaryColor = theme.palette.secondary.main;
      const successColor = theme.palette.success.main;
      const warningColor = theme.palette.warning.main;

      return (
        <Box
          sx={{
            p: 2.5,
            maxWidth: 380,
            bgcolor: bgColor,
            color: textPrimary,
            borderRadius: 2,
            boxShadow: theme.shadows[4],
            border: 'none',
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            sx={{
              color: primaryColor,
              fontSize: '0.95rem',
              mb: 1.5,
              letterSpacing: '0.01em',
            }}
          >
            Model Configuration
          </Typography>
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="body2"
              display="block"
              sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              <Box
                component="span"
                sx={{
                  color: textSecondary,
                  fontWeight: 500,
                  mr: 1.5,
                  minWidth: 90,
                  display: 'inline-block',
                }}
              >
                Provider:
              </Box>
              <Box
                component="span"
                sx={{ color: textPrimary, fontWeight: 400 }}
              >
                {modelConfig.provider}
              </Box>
            </Typography>
            <Typography
              variant="body2"
              display="block"
              sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              <Box
                component="span"
                sx={{
                  color: textSecondary,
                  fontWeight: 500,
                  mr: 1.5,
                  minWidth: 90,
                  display: 'inline-block',
                }}
              >
                Model:
              </Box>
              <Box
                component="span"
                sx={{ color: textPrimary, fontWeight: 400 }}
              >
                {modelConfig.model}
              </Box>
            </Typography>
            {modelConfig.temperature !== undefined && (
              <Typography
                variant="body2"
                display="block"
                sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
              >
                <Box
                  component="span"
                  sx={{
                    color: textSecondary,
                    fontWeight: 500,
                    mr: 1.5,
                    minWidth: 90,
                    display: 'inline-block',
                  }}
                >
                  Temperature:
                </Box>
                <Box
                  component="span"
                  sx={{ color: warningColor, fontWeight: 500 }}
                >
                  {modelConfig.temperature}
                </Box>
              </Typography>
            )}
            {modelConfig.max_tokens !== undefined && (
              <Typography
                variant="body2"
                display="block"
                sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
              >
                <Box
                  component="span"
                  sx={{
                    color: textSecondary,
                    fontWeight: 500,
                    mr: 1.5,
                    minWidth: 90,
                    display: 'inline-block',
                  }}
                >
                  Max Tokens:
                </Box>
                <Box
                  component="span"
                  sx={{ color: warningColor, fontWeight: 500 }}
                >
                  {modelConfig.max_tokens}
                </Box>
              </Typography>
            )}
          </Box>

          {/* Output Format Section */}
          {version?.output_format && (
            <>
              <Divider sx={{ my: 1.5, borderColor: textSecondary }} />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{
                  color: primaryColor,
                  fontSize: '0.95rem',
                  mb: 1,
                  letterSpacing: '0.01em',
                }}
              >
                Output Format
              </Typography>
              <Typography
                variant="body2"
                display="block"
                sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
              >
                <Box
                  component="span"
                  sx={{
                    color: textSecondary,
                    fontWeight: 500,
                    mr: 1.5,
                    minWidth: 90,
                    display: 'inline-block',
                  }}
                >
                  Format:
                </Box>
                <Box
                  component="span"
                  sx={{ color: textPrimary, fontWeight: 400 }}
                >
                  {OUTPUT_FORMATS.find(f => f.value === version.output_format)
                    ?.label || version.output_format}
                </Box>
              </Typography>
              {OUTPUT_FORMATS.find(f => f.value === version.output_format)
                ?.description && (
                <Typography
                  variant="body2"
                  display="block"
                  sx={{
                    mb: 1,
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    color: textSecondary,
                    fontStyle: 'italic',
                  }}
                >
                  {
                    OUTPUT_FORMATS.find(f => f.value === version.output_format)
                      .description
                  }
                </Typography>
              )}
            </>
          )}

          {policies.length > 0 && (
            <>
              <Divider sx={{ my: 1.5, borderColor: textSecondary }} />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{
                  color: primaryColor,
                  fontSize: '0.95rem',
                  mb: 1,
                  letterSpacing: '0.01em',
                }}
              >
                Security Policies
              </Typography>
              <Box>
                {policies.map((policy, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    display="block"
                    sx={{ mb: 0.75, fontSize: '0.875rem', lineHeight: 1.6 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: secondaryColor,
                        mr: 1,
                        fontWeight: 500,
                      }}
                    >
                      • {policy.name}
                    </Box>
                    <Box
                      component="span"
                      sx={{ color: successColor, fontWeight: 400 }}
                    >
                      ({policy.action})
                    </Box>
                  </Typography>
                ))}
              </Box>
            </>
          )}

          {policies.length === 0 && (
            <>
              <Divider sx={{ my: 1.5, borderColor: textSecondary }} />
              <Typography variant="body2" color={textSecondary}>
                No security policies assigned
              </Typography>
            </>
          )}
        </Box>
      );
    },
    [theme, formatModelConfig, formatSecurityPolicies, showTooltip]
  );

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = event => {
      if (versionDropdownOpen) {
        return; // Don't interfere when dropdown is open
      }
      // Close tooltip on any click
      setTooltipOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [versionDropdownOpen, showTooltip]);

  // Find selected version object (handle both string and number comparisons)
  const selectedVersion = versions.find(v => {
    const versionValue = getVersionValueDefault(v);
    // Handle both string and number comparisons
    return (
      versionValue === value ||
      String(versionValue) === String(value) ||
      Number(versionValue) === Number(value)
    );
  });

  // Format date for display
  const formatDate = date => {
    if (!date) return null;
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return null;
    }
  };

  // Handle version change
  const handleChange = e => {
    const versionValue = e.target.value;
    const versionObj = versions.find(v => {
      const vValue = getVersionValueDefault(v);
      return (
        vValue === versionValue ||
        String(vValue) === String(versionValue) ||
        Number(vValue) === Number(versionValue)
      );
    });
    // Close dropdown and tooltip before calling onChange
    setVersionDropdownOpen(false);
    setTooltipOpen(false);
    // Call onChange to update parent component state
    onChange(versionValue, versionObj);
  };

  const selectComponent = (
    <Select
      value={value !== null && value !== undefined ? value : ''}
      label={label}
      open={versionDropdownOpen}
      onOpen={() => {
        setVersionDropdownOpen(true);
        setTooltipOpen(false); // Close tooltip when dropdown opens
      }}
      onClose={() => {
        setVersionDropdownOpen(false);
        setTooltipOpen(false);
      }}
      onBlur={() => {
        // Close tooltip on blur, but don't interfere with dropdown closing
        // The dropdown is already closed via onClose handler
        setTooltipOpen(false);
      }}
      onMouseLeave={() => {
        // Only close tooltip if dropdown is not open
        if (!versionDropdownOpen) {
          setTooltipOpen(false);
        }
      }}
      onChange={handleChange}
      displayEmpty={displayEmpty}
      renderValue={selectedValue => {
        // Handle empty value
        if (!selectedValue || selectedValue === '') {
          return displayEmpty ? 'Select a template version' : '';
        }

        const version = versions.find(v => {
          const versionValue = getVersionValueDefault(v);
          return (
            versionValue === selectedValue ||
            String(versionValue) === String(selectedValue) ||
            Number(versionValue) === Number(selectedValue)
          );
        });

        if (!version) {
          return displayEmpty
            ? 'Select a template version'
            : `Version ${selectedValue}`;
        }

        const versionNum = getVersionNumberDefault(version);
        const status = getVersionStatusDefault(version);
        const date = formatDate(getVersionDateDefault(version));

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>Version {versionNum}</span>
            {date && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                ({date})
              </Typography>
            )}
            {status && (
              <StatusChip
                label={status}
                color={getStatusColor(status)}
                status={status}
                sx={{
                  width: 70,
                  height: 20,
                  fontSize: '0.65rem',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0,
                  },
                }}
              />
            )}
          </Box>
        );
      }}
    >
      {displayEmpty && (
        <MenuItem value="" disabled>
          Select a template version
        </MenuItem>
      )}
      {versions.length === 0 ? (
        <MenuItem disabled>Loading versions...</MenuItem>
      ) : (
        versions.map(version => {
          const versionValue = getVersionValueDefault(version);
          const versionNum = getVersionNumberDefault(version);
          const status = getVersionStatusDefault(version);
          const date = formatDate(getVersionDateDefault(version));
          const isDark = theme.palette.mode === 'dark';
          // Use string key for React to force re-render when dropdown state changes
          const versionKey = `version-${versionValue}-${versionDropdownOpen ? 'open' : 'closed'}`;

          const menuItem = (
            <MenuItem key={`menuitem-${versionValue}`} value={versionValue}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <span>Version {versionNum}</span>
                {date && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    ({date})
                  </Typography>
                )}
                {status && (
                  <StatusChip
                    label={status}
                    color={getStatusColor(status)}
                    status={status}
                    sx={{
                      width: 70,
                      height: 20,
                      fontSize: '0.65rem',
                      '& .MuiChip-label': {
                        px: 1,
                        py: 0,
                      },
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          );

          // Wrap with tooltip if enabled, but only when dropdown is closed
          // When dropdown is open, don't wrap with tooltip to prevent blocking clicks
          // This ensures MenuItem clicks work properly when dropdown is open
          if (showTooltip && !versionDropdownOpen) {
            return (
              <Tooltip
                key={`tooltip-${versionKey}`}
                title={createVersionTooltip(version)}
                arrow
                placement="right"
                enterDelay={300}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'transparent',
                      boxShadow: 'none',
                      padding: 0,
                      maxWidth: 'none',
                    },
                  },
                  arrow: {
                    sx: {
                      color: isDark
                        ? theme.palette.grey[900]
                        : theme.palette.background.paper,
                    },
                  },
                }}
              >
                <Box component="span" sx={{ display: 'block', width: '100%' }}>
                  {menuItem}
                </Box>
              </Tooltip>
            );
          }

          // When dropdown is open or tooltip is disabled, render without tooltip wrapper
          // This allows MenuItem to receive clicks directly without interference
          return menuItem;
        })
      )}
    </Select>
  );

  // Wrap with tooltip for selected value if enabled
  // Only show tooltip when dropdown is closed to avoid blocking interactions
  if (showTooltip && selectedVersion) {
    return (
      <FormControl size={size} fullWidth={fullWidth}>
        <InputLabel>{label}</InputLabel>
        <Tooltip
          title={
            selectedVersion && !versionDropdownOpen
              ? createVersionTooltip(selectedVersion)
              : ''
          }
          arrow
          placement="right"
          enterDelay={300}
          // Disable tooltip completely when dropdown is open to prevent blocking clicks
          disableHoverListener={versionDropdownOpen}
          disableFocusListener={versionDropdownOpen}
          disableTouchListener={versionDropdownOpen}
          open={tooltipOpen && !versionDropdownOpen && selectedVersion !== null}
          onOpen={() => {
            // Only open tooltip if dropdown is closed
            if (!versionDropdownOpen) {
              setTooltipOpen(true);
            }
          }}
          onClose={() => setTooltipOpen(false)}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'transparent',
                boxShadow: 'none',
                padding: 0,
                maxWidth: 'none',
                // Ensure tooltip doesn't block pointer events when dropdown is open
                pointerEvents: versionDropdownOpen ? 'none' : 'auto',
              },
            },
            arrow: {
              sx: {
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[900]
                    : theme.palette.background.paper,
              },
            },
          }}
        >
          {selectComponent}
        </Tooltip>
      </FormControl>
    );
  }

  return (
    <FormControl size={size} fullWidth={fullWidth}>
      <InputLabel>{label}</InputLabel>
      {selectComponent}
    </FormControl>
  );
};

export default TemplateVersionSelect;
